// The immersive Git client's thin bottom status strip (design 2.5): a compact,
// always-visible summary of the active repository sitting below the three-pane
// PanelGroup. It shows the repo basename (full path on hover), the current
// branch, the ahead/behind sync state versus the upstream, and a transient
// operation label (e.g. "Pushing") while an action from the header toolbar is in
// flight. Purely presentational; renders nothing when no repository resolves.

import type { ReactElement } from 'react';
import { GitBranch } from 'lucide-react';
import { basename } from '../../fs/containment';

export interface GitStatusStripProps {
  /** The active repo root, or null when no repository resolves. */
  root: string | null;
  /** Current branch of the active repo, when known. */
  branch?: string;
  /** Commits ahead of the upstream. */
  ahead?: number;
  /** Commits behind the upstream. */
  behind?: number;
  /** Transient operation label (e.g. "Fetching"); hidden when null/empty. */
  op?: string | null;
}

/** Human ahead/behind summary: "up to date", "2 ahead", "1 behind", "2 ahead, 1 behind". */
function syncLabel(ahead = 0, behind = 0): string {
  const parts: string[] = [];
  if (ahead > 0) parts.push(`${ahead} ahead`);
  if (behind > 0) parts.push(`${behind} behind`);
  return parts.length === 0 ? 'up to date' : parts.join(', ');
}

export function GitStatusStrip({
  root,
  branch,
  ahead,
  behind,
  op,
}: GitStatusStripProps): ReactElement | null {
  if (!root) return null;

  const name = basename(root) || root;

  return (
    <footer className="mp-git-statusstrip">
      <span className="mp-git-statusstrip__repo" title={root}>
        {name}
      </span>
      {branch ? (
        <span className="mp-git-statusstrip__branch" title={`On branch ${branch}`}>
          <GitBranch size={12} aria-hidden className="mp-git-statusstrip__branch-icon" />
          {branch}
        </span>
      ) : null}
      <span className="mp-git-statusstrip__sync">{syncLabel(ahead, behind)}</span>
      {op ? (
        <span className="mp-git-statusstrip__op" role="status">
          {op}
        </span>
      ) : null}
    </footer>
  );
}
